import { Images } from '@/assets/images';
import { DocTypeEnum, GenderEnum, REGEX } from '@/constant';
import { cls, dayjs, formatDate } from '@/utils';

import { isNotNilOrEmpty } from 'ramda-adjunct';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	frName: '',
	frNationality: '',
	frGender: '',
	frDateOfBirth: '',
	frHomeCountryAddr: '',
	frPhoneNo: '',
	occupation: '',
	desiredWorkplace: '',
	desiredPeriod: '',
	participationRegistYear: '',
	participationDateApplication: '',
	recommenderName: '',
	recommenderRegistNo: '',
	recommenderPhone: '',
	recommenderRelational: '',
	participationApplicant: '',
	participationApplicantSignature: '',
};

type IValues = typeof initializeValue;

export const PARENTS_OF_INTERNATIONAL_STUDENTS_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.ARF_09
> = {
	initializeValue,
	mapPropToValues({ detailData, applicantData, recommenderData, unitData }) {
		const { unitInfo, recommender } = detailData || {};

		const currentYear = dayjs().year().toString();

		return {
			...initializeValue,
			frName: applicantData?.fullName || '',
			participationApplicantSignature: applicantData?.fullName || '',
			frNationality: applicantData?.nationalName || '',
			frGender: applicantData?.gender || '',
			frDateOfBirth: applicantData?.birthDate || '',
			frPhoneNo: applicantData?.phone || '',
			frHomeCountryAddr: applicantData?.hometownAddr || '',
			participationRegistYear: currentYear,
			recommenderName: recommenderData?.name || '',
			recommenderRegistNo: isNotNilOrEmpty(recommenderData?.birthDate)
				? `${formatDate(recommenderData?.birthDate, {
						outputFormat: 'YYMMDD',
					})}-`
				: '',
			recommenderPhone: recommenderData?.phone || '',
			recommenderRelational:
				(recommenderData?.id === recommender?.[0]?.recommenderId
					? recommender?.[0]?.recommenderRelationship
					: '') || '',

			desiredWorkplace: unitData?.countyDistrict
				? unitData?.countyDistrict
				: unitInfo?.countyDistrict || '',

			desiredPeriod: formatDate(dayjs(), {
				outputFormat: 'YYYY',
			}),

			participationDateApplication: formatDate(dayjs()),
		};
	},
	title: '계절근로 참여 신청서(유학생[D-2]의 부모)',
	pages: [
		{
			background: {
				src: Images.formParentsOfInternationalStudents,
				className: 'template',
				dataSource: Images.formParentsOfInternationalStudents,
			},
			textAreas: [
				{
					name: 'frName',
					maxHeight: 53,
					maxLength: 44,
					containerClassName: 'absolute top-[284px] left-[171px] w-[204px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'frNationality',
					maxHeight: 60,
					maxLength: 18,
					containerClassName: 'absolute top-[283px] left-[454px] w-[81px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'occupation',
					maxHeight: 53,
					maxLength: 20,
					containerClassName: 'absolute right-[10.5%] top-[347px] w-[110px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'frHomeCountryAddr',
					maxHeight: 53,
					maxLength: 72,
					containerClassName: 'absolute left-[28%] top-[408px] w-[270px]',
				},
				{
					name: 'frPhoneNo',
					maxHeight: 55,
					maxLength: 20,
					containerClassName: 'absolute left-[605px] top-[406px] w-[99px]',
				},
				(values, index) => ({
					name: 'desiredWorkplace',
					maxHeight: 50,
					maxLength: 60,
					className: 'text-center text-[17.33px] leading-none',
					containerClassName: cls(
						'absolute left-[40.5%] top-[520px] w-[389px]',
						values?.desiredWorkplace && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				(values, index) => ({
					name: 'desiredPeriod',
					maxHeight: 48,
					maxLength: 20,
					containerClassName: cls(
						'absolute left-[325px] top-[576px] w-[380px]',
						values?.desiredPeriod && 'bg-white'
					),
					className: 'text-center text-[17.33px] leading-none',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				}),
				{
					name: 'recommenderName',
					maxHeight: 73,
					maxLength: 36,
					containerClassName: 'absolute left-[28.4%] top-[677px] w-[140px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'recommenderRegistNo',
					maxHeight: 73,
					maxLength: 33,
					containerClassName: 'absolute left-[73.6%] top-[677px] w-[125px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'recommenderPhone',
					maxHeight: 56,
					maxLength: 20,
					containerClassName: 'absolute left-[28.4%] top-[753px] w-[140px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'recommenderRelational',
					maxHeight: 73,
					maxLength: 33,
					containerClassName: 'absolute left-[73.6%] top-[743px] w-[125px]',
					className: 'text-[14.67px] leading-none',
				},
				{
					name: 'participationRegistYear',
					maxHeight: 30,
					maxLength: 4,
					containerClassName: 'absolute left-[212px] top-[825px] w-[35px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'participationRegistYear',
					maxHeight: 30,
					maxLength: 4,
					containerClassName: 'absolute left-[616px] top-[860px] w-[35px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'participationRegistYear',
					maxHeight: 30,
					maxLength: 4,
					containerClassName: 'absolute left-[383px] top-[894px] w-[35px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'participationApplicantSignature',
					maxHeight: 53,
					maxLength: 50,
					containerClassName: 'absolute w-[235px] top-[983px] left-[265px]',
					className: 'text-[14.67px] leading-none',
				},
			],
			checkboxes: [
				{
					name: 'frGender',
					options: Object.values(GenderEnum),
					classNames: [
						'absolute top-[293px] left-[618px]',
						'absolute top-[313px] left-[618px]',
					],
					iconType: 'circle',
				},
			],
			datePickers: [
				{
					name: 'frDateOfBirth',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:!text-center w-[100%]',
					containerClassName: 'absolute left-[28%] top-[32%] w-[270px]',
					style: {
						width: '100%',
						textAlign: 'center',
					},
				},
				{
					name: 'participationDateApplication',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:!text-center w-[100%]',
					containerClassName: 'absolute left-[107px] top-[1000px]',
					style: {
						width: '100%',
						textAlign: 'center',
					},
				},
			],

			signatures: [
				{
					name: 'participationApplicant',
					className: 'absolute w-[195px] h-[55px] top-[983px] left-[512px]',
				},
			],
			highlightBoxes: [
				{
					name: 'frName',
					className: 'top-[284px] left-[97px] w-[55px]',
				},
				{
					name: 'frNationality',
					className: 'top-[284px] left-[396px] w-[42px]',
				},
				{
					name: 'frGender',
					className: 'top-[284px] left-[550px] w-[42px]',
				},
				{
					name: 'frDateOfBirth',
					className: 'top-[347px] left-[111px] w-[75px]',
				},
				{
					name: 'occupation',
					className: 'top-[346px] left-[525px] w-[45px]',
				},
				{
					name: 'frHomeCountryAddr',
					className: 'top-[408px] left-[108px] w-[82px]',
				},
				{
					name: 'frPhoneNo',
					className: 'top-[409px] left-[519px] w-[60px]',
				},
				{
					name: 'desiredWorkplace',
					className: 'top-[525px] left-[107px] w-[113px]',
				},
				{
					name: 'desiredPeriod',
					className: 'top-[576px] left-[108px] w-[115px]',
				},
				{
					name: 'recommenderName',
					className: 'top-[687px] left-[116px] w-[71px]',
				},
				(values, index) => ({
					name: 'recommenderRegistNo',
					className: 'top-[681px] left-[406px] w-[151px] !h-[41px]',
					hasError: values?.recommenderRegistNo?.length < 13,
				}),
				{
					name: 'recommenderPhone',
					className: 'top-[752px] left-[116px] w-[72px] ',
				},
				{
					name: 'recommenderRelational',
					className: 'top-[752px] left-[406px] w-[145px] !h-[24px]',
				},
				{
					name: 'participationRegistYear',
					className: 'top-[827px] left-[205px] w-[42px]',
				},
				{
					name: 'participationRegistYear',
					className: 'top-[861px] left-[598px] w-[68px]',
				},
				{
					name: 'participationRegistYear',
					className: 'top-[894px] left-[366px] w-[64px]',
				},
				{
					name: 'participationDateApplication',
					className: 'top-[930px] left-[132px] w-[81px]',
				},
				{
					name: 'participationApplicantSignature',
					className: 'top-[930px] left-[350px] w-[65px]',
				},
				{
					name: 'participationApplicant',
					className: 'top-[930px] left-[549px] w-[117px]',
				},
			],
		},
	],
};
